import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { orpc, queryClient } from "@/frontend/orpc-client";
import {
  PATIENTS_PAGE_SIZE,
  PATIENTS_REFRESH_INTERVAL_MS,
  type PatientTableFilters,
} from "@/frontend/types/dashboard/patients";

export function usePatientAccountsQuery(patientMembershipId: string) {
  return useQuery(orpc.admin.listUserAccounts.queryOptions({ input: { patientMembershipId } }));
}

export function usePatientRecommendedSourcesQuery(patientMembershipId: string) {
  return useQuery(orpc.admin.listUserRecommendedSources.queryOptions({ input: { patientMembershipId } }));
}

export function useFhirSourcesSearchQuery(query: string, limit: number = 10) {
  const trimmedQuery = query.trim();
  return useQuery({
    ...orpc.fhirSources.search.queryOptions({ input: { query: trimmedQuery, limit } }),
    enabled: trimmedQuery.length >= 2,
  });
}

export function usePatientAuthEventsQuery(
  patientMembershipId: string,
  providerId: string,
  enabled: boolean,
) {
  return useQuery({
    ...orpc.admin.listAuthEvents.queryOptions({ input: { patientMembershipId, providerId } }),
    enabled,
  });
}

export function usePatientMagicLinkQuery(patientMembershipId: string) {
  return useQuery(orpc.admin.getLatestMagicLink.queryOptions({ input: { patientMembershipId } }));
}

export function useMagicLinkActivityQuery(patientMembershipId: string, opts?: { enabled?: boolean }) {
  return useQuery({
    ...orpc.admin.listMagicLinkActivity.queryOptions({ input: { patientMembershipId } }),
    enabled: opts?.enabled ?? true,
    refetchOnMount: "always",
    staleTime: 0,
  });
}

export function useActiveMemberQuery(enabled: boolean) {
  return useQuery({
    ...orpc.org.getActiveMember.queryOptions({ input: {} }),
    enabled,
  });
}

/**
 * Keyset-paginated patient table query. Each chunk carries the previous
 * chunk's `nextCursor`; the filters live in the generated query key (the
 * input closure is evaluated with the initial page param), so changing sort
 * or filters starts a fresh cursor chain instead of replaying a stale cursor.
 * `placeholderData` keeps the previous rows rendered while a new filter's
 * first chunk loads, so filtering never flashes an empty table. The table
 * re-reads every 10 seconds while its tab is visible.
 */
export function usePatientsInfiniteQuery(filters: PatientTableFilters) {
  return useInfiniteQuery({
    ...orpc.admin.listPatients.infiniteOptions({
      input: (cursor: string | null) => ({
        ...filters,
        limit: PATIENTS_PAGE_SIZE,
        cursor: cursor ?? undefined,
      }),
      initialPageParam: null,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    }),
    placeholderData: (previous) => previous,
    refetchInterval: PATIENTS_REFRESH_INTERVAL_MS,
  });
}

/**
 * Starts the summary page's three requests together and doesn't wait on them,
 * so the cards no longer wait for the summary. Each card still shows the moment
 * its own answer arrives.
 * @param patientMembershipId The patient whose summary page is opening.
 */
export function prefetchPatientSummary(patientMembershipId: string): void {
  const input = { patientMembershipId };
  void queryClient.prefetchQuery(orpc.admin.getPatientSummary.queryOptions({ input }));
  void queryClient.prefetchQuery(orpc.admin.listPatientConnections.queryOptions({ input }));
  void queryClient.prefetchQuery(orpc.admin.getPatientActivity.queryOptions({ input }));
}

/** Header, stat tiles and rail badges for one patient. */
export function usePatientSummaryQuery(patientMembershipId: string) {
  return useQuery({
    ...orpc.admin.getPatientSummary.queryOptions({ input: { patientMembershipId } }),
    enabled: !!patientMembershipId,
  });
}

/** Stored resource counts for one patient, by FHIR resource type and by source. */
export function usePatientRecordTypeCountsQuery(patientMembershipId: string) {
  return useQuery({
    ...orpc.admin.getPatientRecordTypeCounts.queryOptions({ input: { patientMembershipId } }),
    enabled: !!patientMembershipId,
  });
}

/**
 * The health systems this workspace opens with when it has suggested none of
 * its own. Shared by the connect surface and the Summary next step, so both
 * show the same systems.
 */
export function useDefaultSuggestedSourcesQuery(enabled: boolean = true) {
  return useQuery({
    ...orpc.admin.getDefaultSuggestedSources.queryOptions({ input: {} }),
    enabled,
  });
}

/**
 * The owner's own patient membership in an individual workspace, found or
 * created. Enabled only there, since `org.ensureSelfPatient` refuses every other
 * workspace type. It is never retried, because its one real failure is a
 * workspace holding somebody else's patient, which retrying cannot change.
 */
export function useSelfPatientQuery(enabled: boolean) {
  return useQuery({
    ...orpc.org.ensureSelfPatient.queryOptions({ input: {} }),
    enabled,
    retry: false,
  });
}

/** Counts behind the patient table's filter chips. */
export function usePatientListCountsQuery() {
  return useQuery(orpc.admin.getPatientListCounts.queryOptions({ input: {} }));
}

/** Connections for one patient, with channel, source, record count and failure state. */
export function usePatientConnectionsQuery(patientMembershipId: string) {
  return useQuery({
    ...orpc.admin.listPatientConnections.queryOptions({ input: { patientMembershipId } }),
    enabled: !!patientMembershipId,
  });
}

export function usePatientSignatureQuery(patientMembershipId: string) {
  return useQuery(orpc.admin.getPatientSignature.queryOptions({ input: { patientMembershipId } }));
}

export function usePatientSignatureHistoryQuery(patientMembershipId: string, enabled: boolean) {
  return useQuery({
    ...orpc.admin.getPatientSignatureHistory.queryOptions({ input: { patientMembershipId } }),
    enabled,
  });
}

export function useRetrievalErrorSummaryQuery(
  patientMembershipId: string,
  accountId: string,
  enabled: boolean,
) {
  return useQuery({
    ...orpc.admin.getRetrievalErrorSummary.queryOptions({ input: { patientMembershipId, accountId } }),
    enabled,
  });
}

export function usePatientRecordsStatsQuery(
  patientMembershipId: string,
  enabled: boolean,
) {
  return useQuery({
    ...orpc.admin.getPatientRecordsStats.queryOptions({ input: { patientMembershipId } }),
    enabled,
  });
}

export function usePatientRetrievalStatsQuery(patientMembershipId: string) {
  return useQuery(orpc.admin.getPatientRetrievalStats.queryOptions({ input: { patientMembershipId } }));
}

export function usePatientRetrievalStatusQuery(patientMembershipId: string) {
  return useQuery({
    ...orpc.admin.getPatientRetrievalStatus.queryOptions({ input: { patientMembershipId } })
  });
}
